import { t } from "@travily/i18n";
import { CameraIcon, ExitIcon } from "@ui/components/Icon";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { DropZone, type DropZoneProps, FileTrigger, useSingleFileDrop } from "@ui/primitives/DropZone";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";

const avatarUpload = tv({
  slots: {
    root: "relative shrink-0 rounded-full",
    // block, not the default inline: an inline span ignores h-full, which
    // squashes the clip region and lets the fallback's corners escape the circle.
    surface: "relative block size-full overflow-clip rounded-full",
    trigger: "absolute inset-0 cursor-pointer rounded-full outline-none",
    preview: "absolute inset-0 size-full object-cover",
    badge: [
      "pointer-events-none absolute right-0 bottom-0 flex size-7 items-center justify-center",
      "rounded-full border-2 border-white bg-neutral-900 text-white",
    ],
    removeButton: [
      "absolute right-0 bottom-0 flex size-7 cursor-pointer items-center justify-center",
      "rounded-full border-2 border-white bg-neutral-900 text-white outline-none",
      "hover:bg-neutral-700 focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2",
    ],
  },
  variants: {
    isDropTarget: {
      true: { root: "ring-2 ring-blue-400 ring-offset-2" },
    },
  },
});

const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

export interface AvatarUploadProps {
  /** Accessible name for the control, e.g. "Photo". */
  label: string;
  /** Shown until a photo is chosen — pass an `<Avatar.Root>` (or any tile) to fill the circle. */
  fallback: ReactNode;
  /** Object URL or remote URL of the chosen photo. */
  previewSrc?: string | null;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  /** Sets the diameter. Defaults to `size-16`. */
  className?: string;
}

/**
 * A single circular photo control: the entity's avatar with a camera badge, which
 * swaps to a remove badge once a photo is chosen. Accepts a click or a drop.
 *
 * Distinct from AvatarPicker, which chooses between several tiles — reach for this
 * when upload is the only option and the field is a compact part of a larger form.
 */
export function AvatarUpload({ label, fallback, previewSrc, onFileSelect, onRemove, className }: AvatarUploadProps) {
  const styles = avatarUpload();
  const { onDrop, onSelect } = useSingleFileDrop(onFileSelect);

  return (
    <DropZone
      aria-label={label}
      className={
        composeRenderProps(className ?? "size-16", (className, renderProps) =>
          styles.root({ ...renderProps, className }),
        ) as DropZoneProps["className"]
      }
      onDrop={onDrop}
    >
      <span className={styles.surface()}>
        {fallback}
        {previewSrc && <img alt="" className={styles.preview()} src={previewSrc} />}
        <FileTrigger acceptedFileTypes={ACCEPTED_IMAGE_TYPES} onSelect={onSelect}>
          {/* Named for the action, not the field — the drop zone around it already
              carries `label`, and two controls sharing one name reads as a duplicate. */}
          <Button
            aria-label={previewSrc ? t("components.avatar_upload.change") : t("components.avatar_upload.add")}
            className={styles.trigger()}
          />
        </FileTrigger>
      </span>
      {previewSrc ? (
        <Button aria-label={t("components.avatar_upload.remove")} className={styles.removeButton()} onPress={onRemove}>
          <ExitIcon aria-hidden="true" className="size-3.5" />
        </Button>
      ) : (
        <span aria-hidden="true" className={styles.badge()}>
          <CameraIcon className="size-3.5" />
        </span>
      )}
    </DropZone>
  );
}
